import React, { useCallback, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Grid,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import {
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  GetContractsDocument,
  GetContractsQuery,
  GetContractsQueryVariables,
  RecurringTaskFrequency,
  RecurringTaskFragmentFragment,
} from '../generated/graphql';
import { useForm, Controller } from 'react-hook-form';

interface NewRecurringTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description?: string;
    frequency: RecurringTaskFrequency;
    nextDate: string;
    stopAfterDate?: string;
    dueDays: number;
    unitId?: string;
    contractId?: string;
  }) => void;
  loading: boolean;
  unitId?: string;
  contractId?: string;
  editTask?: RecurringTaskFragmentFragment;
}

interface RecurringTaskFormValues {
  title: string;
  description: string;
  frequency: RecurringTaskFrequency;
  nextDate: string;
  stopAfterDate: string;
  dueDays: string;
  entityType: 'unit' | 'contract' | '';
  unitId: string;
  contractId: string;
}

const frequencyOptions: { value: RecurringTaskFrequency; label: string }[] = [
  { value: 'W', label: 'Týždenne' },
  { value: 'W2', label: 'Každé 2 týždne' },
  { value: 'M', label: 'Mesačne' },
  { value: 'M2', label: 'Každé 2 mesiace' },
  { value: 'M3', label: 'Každé 3 mesiace' },
  { value: 'M6', label: 'Polročne' },
  { value: 'Y', label: 'Ročne' },
  { value: 'Y2', label: 'Každé 2 roky' },
  { value: 'Y3', label: 'Každé 3 roky' },
  { value: 'Y4', label: 'Každé 4 roky' },
  { value: 'Y5', label: 'Každých 5 rokov' },
];

const dueDaysOptions = Array.from({ length: 31 }, (_, i) => ({ value: i.toString(), label: `${i} dní` }));

export function NewRecurringTaskDialog({
  open,
  onClose,
  onSubmit,
  loading,
  unitId: initialUnitId,
  contractId: initialContractId,
  editTask,
}: NewRecurringTaskDialogProps) {
  const getDefaultValues = useCallback((): RecurringTaskFormValues => {
    const today = new Date().toISOString().substring(0, 10);
    return {
      title: editTask?.title || '',
      description: editTask?.description || '',
      frequency: (editTask?.frequency as RecurringTaskFrequency) || 'M',
      nextDate: editTask?.nextDate ? new Date(editTask.nextDate).toISOString().substring(0, 10) : today,
      stopAfterDate: editTask?.stopAfterDate ? new Date(editTask.stopAfterDate).toISOString().substring(0, 10) : '',
      dueDays: editTask?.dueDays?.toString() || '7',
      entityType: initialUnitId
        ? 'unit'
        : initialContractId
          ? 'contract'
          : editTask?.unitId
            ? 'unit'
            : editTask?.contractId
              ? 'contract'
              : '',
      unitId: initialUnitId || editTask?.unitId || '',
      contractId: initialContractId || editTask?.contractId || '',
    };
  }, [editTask, initialUnitId, initialContractId]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<RecurringTaskFormValues>({
    defaultValues: getDefaultValues(),
  });

  const { data: unitsData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });

  const { data: contractsData } = useQuery<GetContractsQuery, GetContractsQueryVariables>(GetContractsDocument);

  useEffect(() => {
    if (open) {
      reset(getDefaultValues());
    }
  }, [open, reset, getDefaultValues]);

  const entityType = watch('entityType');

  const onFormSubmit = (values: RecurringTaskFormValues) => {
    const data: Parameters<NewRecurringTaskDialogProps['onSubmit']>[0] = {
      title: values.title.trim(),
      frequency: values.frequency,
      nextDate: new Date(values.nextDate).toISOString(),
      dueDays: parseInt(values.dueDays, 10),
    };

    if (values.description) {
      data.description = values.description;
    }
    if (values.stopAfterDate) {
      data.stopAfterDate = new Date(values.stopAfterDate).toISOString();
    }
    if (entityType === 'unit' && values.unitId) {
      data.unitId = values.unitId;
    } else if (entityType === 'contract' && values.contractId) {
      data.contractId = values.contractId;
    }

    onSubmit(data);
  };

  // Filter units: status not archived
  const availableUnits = unitsData?.getUnits.filter((p) => p.status !== 'archived') || [];

  // Filter contracts: not archived (no terminationDate) and not deleted
  const availableContracts = contractsData?.getContracts.filter((c) => !c.terminationDate && !c.deletedAt) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogTitle>{editTask ? 'Upraviť pravidelnú úlohu' : 'Pridať pravidelnú úlohu'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  {...register('title', { required: 'Názov je povinný' })}
                  autoFocus
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  label="Názov"
                  fullWidth
                  variant="standard"
                  disabled={loading}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  {...register('description')}
                  label="Popis"
                  multiline
                  rows={3}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="standard" required>
                  <InputLabel>Frekvencia</InputLabel>
                  <Controller
                    name="frequency"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Frekvencia" disabled={loading}>
                        {frequencyOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  {...register('nextDate', { required: 'Dátum ďalšej úlohy je povinný' })}
                  label="Dátum ďalšej úlohy"
                  type="date"
                  error={!!errors.nextDate}
                  helperText={errors.nextDate?.message}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="standard" required>
                  <InputLabel>Počet dní na splnenie</InputLabel>
                  <Controller
                    name="dueDays"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Počet dní na splnenie" disabled={loading}>
                        {dueDaysOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  {...register('stopAfterDate')}
                  label="Zastaviť spracovanie po dátume"
                  type="date"
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {!initialUnitId && !initialContractId && !editTask && (
              <>
                <FormControl fullWidth variant="standard">
                  <InputLabel>Prepojenie</InputLabel>
                  <Controller
                    name="entityType"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Prepojenie" disabled={loading}>
                        <MenuItem value="">Žiadne</MenuItem>
                        <MenuItem value="unit">Nehnuteľnosť</MenuItem>
                        <MenuItem value="contract">Prenájom</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>

                {entityType === 'unit' && (
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Nehnuteľnosť</InputLabel>
                    <Controller
                      name="unitId"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Nehnuteľnosť" disabled={loading}>
                          <MenuItem value="">Žiadna</MenuItem>
                          {availableUnits.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                )}

                {entityType === 'contract' && (
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Prenájom</InputLabel>
                    <Controller
                      name="contractId"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Prenájom" disabled={loading}>
                          <MenuItem value="">Žiadny</MenuItem>
                          {availableContracts.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.title}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {editTask ? 'Uložiť' : 'Pridať'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
